import type { BusinessProfileDraft } from '@mouja/domain/analysis';
import type { OutputLocale } from '@mouja/domain/merchant-intake';
import type { MerchantAnalysisProgress } from '@mouja/domain/merchant-analysis-progress';

export type {
  MerchantAnalysisProgress,
  MerchantAnalysisStepKey,
  MerchantAnalysisStepStatus,
} from '@mouja/domain/merchant-analysis-progress';

import { request, ApiFailure } from '../workspace/api.js';

/** A read-only deployment capability; unavailable or old servers stay disabled. */
export async function instagramBusinessEntryEnabled(
  signal?: AbortSignal,
): Promise<boolean> {
  try {
    const capability = await request<{ instagramBusinessEnabled?: boolean }>(
      'GET',
      '/merchant/intake-capabilities',
      signal ? { signal } : {},
    );
    return capability.instagramBusinessEnabled === true;
  } catch {
    return false;
  }
}

/** Deployment capability only. A missing endpoint is the pre-rollout server;
 * transient errors must never choose the legacy approve-and-dispatch path. */
export async function firstIdeaEntryEnabled(signal?: AbortSignal): Promise<boolean> {
  try {
    return (
      await request<{ enabled: boolean }>(
        'GET',
        '/merchant/idea-journey-capabilities',
        signal ? { signal } : {},
      )
    ).enabled;
  } catch (error) {
    if (error instanceof ApiFailure && error.kind === 'missing') return false;
    throw error;
  }
}

/** The profile draft the merchant can correct; the domain owns its shape. */
export type BusinessProfileDraftLike = BusinessProfileDraft;

export const MERCHANT_INTAKE_STORAGE_KEY = 'mouja:merchant-intake-id';
export const MERCHANT_PROFILE_INTENT_STORAGE_KEY = 'mouja:merchant-profile-intent';

export type MerchantIntakeStatus =
  | 'COLLECTING'
  | 'PROCESSING'
  | 'READY'
  | 'NEEDS_REVIEW'
  | 'FAILED'
  | 'CLAIMED'
  | 'EXPIRED';

export interface MerchantProfileView {
  readonly id: string;
  readonly status: MerchantIntakeStatus;
  readonly contextNote: string | null;
  readonly claimed: boolean;
  readonly expiresAt: string;
  readonly business: {
    readonly displayName: string;
    readonly sources: readonly {
      readonly platform: string;
      readonly canonicalUrl: string;
      readonly externalIdentity: string | null;
    }[];
  };
  readonly analysis: {
    readonly progress: MerchantAnalysisProgress;
  };
  readonly profileIssue: {
    readonly state: 'none' | 'mouja_checking' | 'more_information_needed';
  };
  /** Historical Instagram collection/cache metadata; absent for website views. */
  readonly sourceObservation?: {
    readonly collectedAt: string;
    readonly oldestCachedAt: string | null;
  };
  /** Social acquisition recovery; absent for historical website views. */
  readonly sourceRecovery?: {
    readonly reason:
      'UNAVAILABLE' | 'ANALYSIS_UNAVAILABLE' | 'PARTIAL' | 'ACCOUNT_AMBIGUOUS';
  };
  readonly profile: {
    readonly artifactId: string;
    readonly payload: BusinessProfileDraft;
    readonly reviewState: 'DRAFT' | 'APPROVED';
  } | null;
  readonly result: {
    readonly handoffArtifactId: string | null;
    /** Absent on older builds; which Phase 1 strategy released the directions. */
    readonly strategy?: 'CONTENT_RESEARCH_V1' | 'GOOGLE_FIRST';
    readonly recommendations: readonly {
      readonly artifactId: string;
      readonly rank: number;
      readonly title: string;
      /** Content research v1 only: the evidence basis of this idea. */
      readonly basis?: 'LIVE_REFERENCE' | 'SAVED_REFERENCE' | 'BUSINESS_BASED' | null;
      readonly hook?: string | null;
      /** Proposed treatment, never observed evidence; null when the idea has none. */
      readonly creativeTreatment?: string | null;
      readonly whyRelevant?: string | null;
      readonly outputLocale?: 'en' | 'ar' | null;
      readonly references?: readonly {
        readonly referenceId: string | null;
        readonly platform: string;
        readonly canonicalUrl: string;
        readonly creatorHandle: string | null;
        readonly creatorAttributionUrl: string | null;
        readonly mediaKind: string;
        readonly caption: string | null;
        readonly sourceLanguage: string;
        readonly category: 'DIRECT' | 'ADJACENT' | null;
        readonly origin: 'LIVE' | 'SAVED' | null;
        readonly observedAt: string | null;
        readonly snapshot: {
          readonly observedAt: string | null;
          readonly measurements: readonly {
            readonly name: string;
            readonly semantics: string;
            readonly status: string;
            readonly value: number | null;
          }[];
        } | null;
        readonly playback: {
          readonly method: string;
          readonly resolution: string;
          readonly playback: string;
        } | null;
      }[];
      readonly objective: string;
      readonly angle: string;
      readonly targetAudienceOrNeed: readonly string[];
      readonly proofStructure: string;
      readonly ctaDirection: string | null;
      readonly whyItFits: readonly string[];
      readonly rankingRationale: string;
      readonly limitations: readonly string[];
      readonly evidenceReferences: readonly string[];
      readonly feedback: {
        readonly artifactId: string;
        readonly decision: 'ACCEPTED' | 'ADJUSTMENT_REQUESTED' | 'REJECTED';
        readonly note: string | null;
        readonly recordedAt: string;
      } | null;
      readonly povExecutability: {
        readonly status: 'POV_READY' | 'NEEDS_INPUT' | 'NOT_POV_SUITABLE';
        readonly reason: string;
      } | null;
    }[];
  } | null;
  /** Absent from views served by builds that predate creative runs. */
  readonly creativeRuns?: readonly MerchantCreativeRunView[];
  readonly actions: {
    readonly profileIsReadOnly: boolean;
    readonly authenticationRequired: boolean;
    /** Absent on older builds; true is the backwards-compatible behaviour. */
    readonly tasteCalibrationEnabled?: boolean;
  };
}

export type ProductionLinePurpose =
  'SALES' | 'GROWTH' | 'LAUNCH' | 'AWARENESS' | 'EDUCATION' | 'OTHER';

export interface ProductionLineSetupView {
  readonly purposeOptions: readonly {
    readonly id: ProductionLinePurpose;
    readonly label: { readonly en: string; readonly ar: string };
    readonly requiresText: boolean;
    readonly state: 'available' | 'disabled';
    readonly reason: string | null;
  }[];
  readonly otherMaxLength: number;
  readonly entitlement: {
    readonly loaded: boolean;
    readonly min: number;
    readonly max: number;
    readonly current: number;
    readonly source: string;
  };
  readonly naming: {
    readonly merchantVisible: false;
    readonly generatedByServer: true;
  };
  readonly continuation: {
    readonly decidedBy: 'server';
    readonly currentAvailability: 'PENDING' | 'AVAILABLE';
  };
  readonly foundation: {
    readonly businessProfileRef: {
      readonly artifactId: string;
      readonly canonicalSha256: string;
    };
    readonly competitorFoundationRef: {
      readonly artifactId: string;
      readonly canonicalSha256: string;
    };
  };
}

/**
 * The brief a Production Line began from — the website and prompt the
 * merchant gave. Null when the server has no intake behind the line.
 */
export interface ProductionLineBriefView {
  /** The intake behind the line; absent on builds that predate it. */
  readonly intakeId?: string;
  readonly websiteUrl: string | null;
  readonly contextNote: string | null;
  readonly updatedAt: string;
}

export type ProductionLineState =
  | 'DRAFT'
  | 'CALIBRATING'
  | 'RECOMMENDATION_READY'
  | 'CONFIGURED'
  | 'ACTIVE'
  | 'PAUSED'
  | 'ARCHIVED';

export interface ProductionLineView {
  readonly productionLine: {
    readonly schemaVersion: 'production-line.v1';
    readonly productionLineId: string;
    readonly merchantId: string;
    readonly configuration: {
      readonly configurationId: string;
      readonly version: number;
      readonly supersedesConfigurationId: string | null;
    };
    readonly title: string;
    readonly purpose: {
      readonly type: ProductionLinePurpose;
      readonly note: string | null;
    };
    readonly businessProfileRef: {
      readonly artifactId: string;
      readonly canonicalSha256: string;
    };
    readonly competitorFoundationRef: {
      readonly artifactId: string;
      readonly canonicalSha256: string;
    };
    readonly videosPerDay: number;
    readonly standingInstruction: string | null;
    readonly selectedDirectionRef: {
      readonly selectionArtifactId: string;
      readonly directionArtifactId: string;
    } | null;
    readonly tasteProfileRef: {
      readonly profileId: string;
      readonly canonicalSha256: string;
    } | null;
    readonly createdAt: string;
    readonly effectiveAt: string;
    readonly state: ProductionLineState;
  };
  readonly configurationCanonicalSha256: string;
  readonly latestChange: {
    readonly overallImpact: string;
    readonly changedFields: readonly {
      readonly field: string;
      readonly impact: string;
    }[];
  } | null;
  readonly capabilities?: {
    readonly tasteCalibration: { readonly enabled: boolean };
  };
  readonly continuation: {
    readonly state: string;
    readonly destination: string | null;
    readonly reason: string | null;
  };
  readonly editor: {
    readonly available: boolean;
    readonly reason: string | null;
    readonly purposeOptions: ProductionLineSetupView['purposeOptions'];
    readonly otherMaxLength: number;
    readonly entitlement: {
      readonly min: number;
      readonly max: number;
      readonly current: number;
    };
  };
  /** Absent on builds that predate the brief projection. */
  readonly brief?: ProductionLineBriefView | null;
}

export interface PendingProductionLineView {
  readonly workflowKind?: 'SAVED_BRIEF';
  readonly savedScriptState?: string | null;
  readonly productionLineId: string;
  readonly title: string;
  readonly state: 'ANALYZING' | 'NEEDS_ATTENTION';
  readonly currentStage: string;
  readonly createdAt: string;
  readonly destination: string;
  readonly brief?: ProductionLineBriefView | null;
  /** The intake behind the line; absent on builds that predate it. */
  readonly intakeId?: string;
}

export interface PendingProductionLineDetailView {
  readonly pending: true;
  readonly productionLineId: string;
  readonly intakeId: string;
  readonly title: string;
  readonly state: 'ANALYZING' | 'NEEDS_ATTENTION';
  readonly currentStage: string;
  readonly createdAt: string;
  readonly brief?: ProductionLineBriefView | null;
}

export type ProductionLineReadView =
  ProductionLineView | PendingProductionLineDetailView;

export type ProductionLineMutationView = ProductionLineView;

export type MerchantTasteCalibrationAction =
  'START' | 'LIKE' | 'DISLIKE' | 'SKIP' | 'PREVIOUS' | 'PLAYBACK_FAILED';

export type MerchantTasteCalibrationState =
  | 'NOT_STARTED'
  | 'PREPARING'
  | 'READY_TO_START'
  | 'ACTIVE'
  | 'PLAYBACK_UNAVAILABLE'
  | 'NO_PLAYABLE_REPLACEMENT'
  | 'AWAITING_ADAPTIVE_SELECTION'
  | 'COMPLETE';

export interface MerchantTasteCalibrationView {
  readonly state: MerchantTasteCalibrationState;
  readonly reason: string | null;
  readonly blockers?: readonly string[];
  readonly sessionId?: string;
  readonly expectedLastSequence: number;
  readonly canGoPrevious?: boolean;
  readonly profileRef?: {
    readonly profileId: string;
    readonly canonicalSha256: string;
  };
  readonly continuation?: {
    readonly destination: string;
  };
  readonly candidate: {
    readonly candidateId: string;
    readonly editionId: string;
    readonly occurrenceId: string;
    readonly currentDecision: 'LIKE' | 'DISLIKE' | 'SKIP' | null;
    readonly playback: {
      readonly availability: 'AVAILABLE' | 'UNAVAILABLE' | 'UNKNOWN';
      readonly reference: string | null;
    };
  } | null;
}

export interface MerchantRecommendationRevealView {
  readonly state: 'PREPARING' | 'READY' | 'ACTIVATED' | 'TEMPORARILY_UNAVAILABLE';
  readonly reason: string | null;
  readonly productionLineId: string;
  readonly configurationId: string;
  readonly recommendationSetId: string | null;
  readonly standingInstruction: string | null;
  readonly directions: readonly {
    readonly directionArtifactId: string;
    readonly rank: number;
    readonly videoIdea: string;
    readonly hook: string;
    readonly format: string;
    readonly visualTreatment: string;
    readonly coreMessage: string;
    readonly callToAction: string;
    readonly rankingRationale: string;
    readonly limitations: readonly string[];
  }[];
  readonly selectedDirectionArtifactId: string | null;
}

export type MerchantCreativeState =
  | 'PREPARING'
  | 'REVIEW_CARD'
  | 'CREATING'
  | 'CHECKING_QUALITY'
  | 'READY'
  | 'NEEDS_ONE_ITEM'
  | 'QUALITY_REVIEW'
  | 'DIRECTION_NEEDS_REFRESH'
  | 'COULD_NOT_CREATE'
  | 'CANCELLED';

/** The merchant's four steps, plus done; how far a run has actually got. */
export type MerchantCreativeStage =
  'SCRIPT' | 'VISUALS' | 'ASSEMBLY' | 'QUALITY' | 'DONE';

export interface MerchantCreativeRunView {
  readonly creativeRunId: string;
  readonly directionArtifactId: string;
  readonly merchantState: MerchantCreativeState;
  /**
   * Null when the server cannot say — a run held before any stage ran. The
   * page then shows nothing as complete rather than guessing.
   */
  readonly merchantStage?: MerchantCreativeStage | null;
  readonly card: {
    readonly cardArtifactId: string;
    readonly direction: string;
    readonly hookLine: string;
    readonly whatTheVideoShows: string;
    readonly assets: {
      readonly references: readonly {
        readonly referenceId: string;
        readonly kind: string | null;
        readonly sourceUrl: string | null;
      }[];
      readonly confirmationRequested: boolean;
    };
    readonly whoAppears: 'NO_ONE';
    readonly durationSeconds: number;
    readonly targetPlatforms: readonly string[];
    readonly aspectRatio: string;
    readonly language: string;
    readonly whatMoujaStillNeeds: readonly string[];
  } | null;
  /** Present once the run is delivered; the bytes stream from these URLs. */
  readonly video: {
    readonly completedAt?: string | null;
    readonly videoUrl: string;
    readonly posterUrl: string;
    readonly durationSeconds: number;
    /** True only when a delivery notice actually reached the merchant. */
    readonly notified: boolean;
    readonly limitations: readonly string[];
  } | null;
  readonly neededItem: string | null;
  /** How many "different angle" asks are left; 0 hides the offer. */
  readonly regenerationsRemaining?: number;
  readonly confirmedAt: string | null;
  readonly authorizedAt: string;
}

/**
 * One entry of the merchant's video library: a run in exactly the shape a
 * Production Line's own view uses, plus the line it belongs to. Served as
 * one read model by the server — the browser never fans out per line.
 */
export interface MerchantVideoView extends MerchantCreativeRunView {
  readonly productionLineId: string | null;
  readonly productionLineTitle: string;
  readonly intakeId: string;
}

export async function fetchMerchantVideos(
  signal?: AbortSignal,
): Promise<{ readonly videos: readonly MerchantVideoView[] }> {
  return request('GET', '/merchant/videos', signal === undefined ? {} : { signal });
}

/** The reasons the card offers when the merchant wants another angle. */
export type RegenerationReason =
  'NOT_ON_BRAND' | 'TOO_SALES_HEAVY' | 'WRONG_FOR_MY_AUDIENCE';

export async function createMerchantCreativeRun(
  id: string,
  body: {
    readonly directionArtifactId: string;
    readonly handoffArtifactId: string;
  },
  idempotencyKey: string,
): Promise<{ creativeRunId: string; state: string }> {
  return request('POST', `/merchant/intakes/${encodeURIComponent(id)}/creative-runs`, {
    body,
    idempotencyKey,
  });
}

/** Ask this Production Line for its next video (its next output slot). */
export async function createProductionLineCreativeRun(
  productionLineId: string,
  idempotencyKey: string,
): Promise<{ creativeRunId: string; state: string }> {
  return request(
    'POST',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/creative-runs`,
    { body: {}, idempotencyKey },
  );
}

export async function confirmMerchantCreativeCard(
  id: string,
  creativeRunId: string,
  cardArtifactId: string,
  idempotencyKey: string,
): Promise<{ creativeRunId: string; state: string; confirmedAt: string }> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/creative-runs/${encodeURIComponent(creativeRunId)}/confirmation`,
    { body: { cardArtifactId }, idempotencyKey },
  );
}

export async function regenerateMerchantCreativeCard(
  id: string,
  creativeRunId: string,
  reason: RegenerationReason | null,
  idempotencyKey: string,
): Promise<{ creativeRunId: string; state: string; remaining: number }> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/creative-runs/${encodeURIComponent(creativeRunId)}/regeneration`,
    { body: reason === null ? {} : { reason }, idempotencyKey },
  );
}

/**
 * A Production Line's videos. `intakeId` names the intake behind the line,
 * because the merchant's own card actions are intake-scoped; it is null only
 * while the line has no runs yet.
 */
export async function fetchProductionLineCreativeRuns(
  productionLineId: string,
): Promise<{
  readonly intakeId: string | null;
  readonly creativeRuns: readonly MerchantCreativeRunView[];
}> {
  return request(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/creative-runs`,
  );
}

export async function createMerchantIntake(input: {
  readonly pilotToken?: string;
  readonly contextNote: string;
  /**
   * The language Mouja's interface is in when the merchant submits: the
   * language they will read their analysis in. Always the explicit
   * `LanguageProvider` selection, never the browser's or the website's.
   */
  readonly outputLocale: OutputLocale;
  readonly sources: readonly {
    readonly url: string;
    readonly platform: 'website' | 'instagram' | 'tiktok';
    readonly ownership: 'mine';
  }[];
}): Promise<{ id: string; status: MerchantIntakeStatus; expiresAt: string }> {
  return request('POST', '/merchant/intakes', { body: input });
}

export async function processMerchantIntake(
  id: string,
  signal?: AbortSignal,
): Promise<MerchantProfileView> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/process`,
    signal === undefined ? {} : { signal },
  );
}

export async function fetchMerchantIntake(
  id: string,
  signal?: AbortSignal,
): Promise<MerchantProfileView> {
  return request(
    'GET',
    `/merchant/intakes/${encodeURIComponent(id)}`,
    signal === undefined ? {} : { signal },
  );
}

/** The authenticated merchant's latest persisted Business Profile. */
export async function fetchCurrentMerchantProfile(
  signal?: AbortSignal,
): Promise<MerchantProfileView> {
  return request(
    'GET',
    '/merchant/business-profile',
    signal === undefined ? {} : { signal },
  );
}

export async function claimMerchantIntake(id: string): Promise<MerchantProfileView> {
  return request('POST', `/merchant/intakes/${encodeURIComponent(id)}/claim`);
}

export async function correctMerchantProfile(
  id: string,
  body: {
    readonly expectedArtifactId: string;
    readonly profile: BusinessProfileDraft;
    readonly reason: string;
  },
  idempotencyKey: string,
): Promise<{ draftArtifactId: string }> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/profile/correct`,
    {
      body,
      idempotencyKey,
    },
  );
}

export async function approveMerchantProfile(
  id: string,
  expectedArtifactId: string,
  idempotencyKey: string,
): Promise<{ approvedArtifactId: string; profileVersion: number }> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/profile/approve`,
    {
      body: { expectedArtifactId },
      idempotencyKey,
    },
  );
}

export async function continueMerchantAnalysis(
  id: string,
): Promise<MerchantProfileView> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/analysis/continue`,
  );
}

export async function recordMerchantRecommendationFeedback(
  id: string,
  body: {
    readonly recommendationArtifactId: string;
    readonly decision: 'ACCEPTED' | 'ADJUSTMENT_REQUESTED' | 'REJECTED';
    readonly note?: string;
  },
  idempotencyKey: string,
): Promise<{
  feedback: {
    artifactId: string;
    recommendationArtifactId: string;
    decision: 'ACCEPTED' | 'ADJUSTMENT_REQUESTED' | 'REJECTED';
    note: string | null;
    recordedAt: string;
  };
}> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(id)}/recommendations/feedback`,
    { body, idempotencyKey },
  );
}

export async function fetchProductionLineSetup(
  intakeId: string,
): Promise<ProductionLineSetupView> {
  return request(
    'GET',
    `/merchant/intakes/${encodeURIComponent(intakeId)}/production-lines/setup`,
  );
}

export async function createProductionLine(
  intakeId: string,
  body: {
    readonly purpose: {
      readonly type: ProductionLinePurpose;
      readonly note: string | null;
    };
    readonly videosPerDay: number;
  },
  idempotencyKey: string,
): Promise<ProductionLineMutationView> {
  return request(
    'POST',
    `/merchant/intakes/${encodeURIComponent(intakeId)}/production-lines`,
    { body, idempotencyKey },
  );
}

export async function fetchProductionLines(): Promise<{
  readonly productionLines: readonly ProductionLineView[];
  readonly pendingProductionLines?: readonly PendingProductionLineView[];
  readonly creation: {
    readonly available: boolean;
    readonly destination: string | null;
  };
}> {
  return request('GET', '/merchant/production-lines');
}

export async function fetchProductionLine(
  productionLineId: string,
): Promise<ProductionLineReadView> {
  return request(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}`,
  );
}

export async function editProductionLine(
  productionLineId: string,
  body: {
    readonly expectedConfigurationId: string;
    readonly purpose?: {
      readonly type: ProductionLinePurpose;
      readonly note: string | null;
    };
    readonly videosPerDay?: number;
    readonly standingInstruction?: string | null;
  },
  idempotencyKey: string,
): Promise<ProductionLineMutationView> {
  return request(
    'PATCH',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}`,
    { body, idempotencyKey },
  );
}

export async function changeProductionLineLifecycle(
  productionLineId: string,
  action: 'pause' | 'resume' | 'archive',
  idempotencyKey: string,
): Promise<ProductionLineMutationView> {
  return request(
    'POST',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/${action}`,
    { idempotencyKey },
  );
}

export async function fetchMerchantTasteCalibration(
  productionLineId: string,
): Promise<MerchantTasteCalibrationView> {
  return request(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/taste-calibration`,
  );
}

export async function actOnMerchantTasteCalibration(
  productionLineId: string,
  body: {
    readonly action: MerchantTasteCalibrationAction;
    readonly expectedCandidateId: string | null;
    readonly expectedLastSequence: number;
  },
  idempotencyKey: string,
): Promise<MerchantTasteCalibrationView> {
  return request(
    'POST',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/taste-calibration/actions`,
    { body, idempotencyKey },
  );
}

export async function fetchMerchantRecommendationReveal(
  productionLineId: string,
): Promise<MerchantRecommendationRevealView> {
  return request(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/recommendations`,
  );
}

export async function activateMerchantRecommendation(
  productionLineId: string,
  body: {
    readonly expectedRecommendationSetId: string;
    readonly expectedConfigurationId: string;
    readonly selectedDirectionArtifactId: string;
    readonly standingInstruction: string | null;
  },
  idempotencyKey: string,
): Promise<MerchantRecommendationRevealView> {
  return request(
    'POST',
    `/merchant/production-lines/${encodeURIComponent(productionLineId)}/recommendations/activate`,
    { body, idempotencyKey },
  );
}
